import { Equal, Option, Schema as S } from "effect";
import { Field, Valid, makeRules, validate } from "foldkit/fieldValidation";
import type { Html, HtmlBuilder } from "foldkit/html";
import { defineMessageUnion } from "foldkit/message";
import * as Mount from "foldkit/mount";
import { defineTaggedUnion } from "foldkit/schema";
import { evo } from "foldkit/struct";
import { defineView } from "foldkit/submodel";
import * as Update from "foldkit/update";

import { ENTRY_TYPES, EntryType } from "../api/schemas/common";
import { ENTRY_SUBTYPE_GROUPS, entrySubtypeLabel, i18next } from "../i18n";
import * as Editor from "./editor";
import { CirclePlus } from "./icons";
import * as Tags from "./tags";

/**
 * v1 `AddEntryView`, as a stateful widget shared by the Record page (U6) and the
 * Entry page (U7). It owns the form only; the page folds `Submitted` into the
 * entry Commands. "Link existing" is not ported.
 *
 * The description editor (CodeMirror Mount, id `"new"`) and the tag Combobox
 * are built inside this view, never through a `viewInputs` slot callback:
 * foldkit scopes slot callbacks to the parent's boundary, so a Mount built in
 * one would dispatch this widget's Messages past its own wrapper and crash.
 * Both end in `ChangedBody` / the comma-joined `tags` string, so submit parses
 * one way.
 */

/** Editor Mount id for the add-entry form; the Record page syncs it on reset. */
export const EDITOR_ID = "new";

export const Model = S.Struct({
	type: EntryType,
	dateExpanded: S.Boolean,
	tagsExpanded: S.Boolean,
	formatExpanded: S.Boolean,
	title: S.String,
	body: S.String,
	tasks: S.Array(S.String),
	file: S.Option(S.instanceOf(File)),
	/** Raw tag text; parsed on submit. */
	tags: S.String,
	tagBox: Tags.Model,
	tagError: S.Option(S.String),
	/** `YYYY-MM-DDTHH:MM` local wall time from the datetime-local input; empty means "now" (R1). */
	occurredOn: Field(S.String),
	error: S.Option(S.String),
	submitting: S.Boolean,
});
export type Model = typeof Model.Type;

const pad = (n: number): string => String(n).padStart(2, "0");
/** The local calendar date as `YYYY-MM-DD`, the value shape of a date input. */
export const localIsoDate = (d: Date): string =>
	`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** The local wall time as `YYYY-MM-DDTHH:MM`, the value shape of a datetime-local input; "" for an invalid date. */
export const localIsoDateTime = (d: Date): string =>
	Number.isNaN(d.getTime())
		? ""
		: `${localIsoDate(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}`;

/** A datetime-local value (local wall time) as a UTC ISO instant, or undefined when it is not one. */
export const localDateTimeToIso = (value: string): string | undefined => {
	const d = new Date(value);
	return value.includes("T") && !Number.isNaN(d.getTime())
		? d.toISOString()
		: undefined;
};

/**
 * KTD1 on the client: the entry timestamp for a chosen occurred-on value. A
 * local date-time is that instant; empty or today keeps the clock's
 * time-of-day; another bare day is noon UTC of that day.
 */
export const occurredAt = (
	occurredOn: Option.Option<string>,
	nowMillis: number,
): string => {
	const now = new Date(nowMillis);
	return Option.match(occurredOn, {
		onNone: () => now.toISOString(),
		onSome: (date) =>
			localDateTimeToIso(date) ??
			(date === "" || date === localIsoDate(now)
				? now.toISOString()
				: `${date}T12:00:00.000Z`),
	});
};

/** `today` comes from the view so `update` never reads the wall clock. */
export const validateOccurredOn = (value: string, today: string) =>
	validate(
		makeRules({
			rules: [
				[
					(v) => v.slice(0, 10) <= today,
					() => i18next.t("entry.occurredOnFuture"),
				],
			],
		}),
	)(value);

export const init = (): Model => ({
	type: "note",
	dateExpanded: false,
	tagsExpanded: false,
	formatExpanded: false,
	title: "",
	body: "",
	tasks: [""],
	file: Option.none(),
	tags: "",
	tagBox: Tags.init("add-entry-tags"),
	tagError: Option.none(),
	occurredOn: Valid({ value: "" }),
	error: Option.none(),
	submitting: false,
});

/** What the page receives on submit. Tags exclude the type; the page adds it. */
export const Request = defineTaggedUnion({
	Note: {
		type: EntryType,
		title: S.String,
		body: S.String,
		tags: S.Array(S.String),
		occurredOn: S.Option(S.String),
	},
	Tasks: {
		title: S.String,
		tasks: S.Array(S.String),
		tags: S.Array(S.String),
		occurredOn: S.Option(S.String),
	},
	Upload: {
		file: S.instanceOf(File),
		tags: S.Array(S.String),
		occurredOn: S.Option(S.String),
	},
});
export type Request = typeof Request.Type;

export const Message = defineMessageUnion({
	ChangedType: { value: EntryType },
	ChangedTitle: { value: S.String },
	ChangedBody: { value: S.String },
	ChangedTask: { index: S.Number, value: S.String },
	ClickedAddTask: {},
	ClickedRemoveTask: { index: S.Number },
	ChangedFile: { file: S.Option(S.instanceOf(File)) },
	ChangedTags: { value: S.String },
	ChangedOccurredOn: { value: S.String, today: S.String },
	GotTagsMessage: { message: Tags.Message },
	ClickedRemoveTag: { tag: S.String },
	ClickedEditorAction: { action: Editor.Action },
	CompletedEditorAction: {},
	ChangedEditorFocus: { focused: S.Boolean },
	ToggledDate: {},
	ToggledTags: {},
	ToggledFormat: {},
	Submitted: {},
});
export type Message = typeof Message.Type;

export const OutMessage = defineMessageUnion({
	Submitted: { request: Request },
});
export type OutMessage = typeof OutMessage.Type;

type Return = Update.ReturnWithOutMessage<Model, Message, OutMessage>;

/** v1 `sanitizeTag`: trimmed, whitespace collapsed, empties dropped, deduped. */
export const parseTags = (raw: string): ReadonlyArray<string> =>
	Array.from(
		new Set(
			raw
				.split(",")
				.map((tag) => tag.trim().replace(/\s+/g, " "))
				.filter((tag) => tag !== ""),
		),
	);

const validTasks = (tasks: ReadonlyArray<string>) =>
	tasks.map((task) => task.trim()).filter((task) => task !== "");

/** v1 `isSubmitDisabled`. */
export const isSubmitDisabled = (model: Model): boolean => {
	if (model.submitting || model.occurredOn._tag === "Invalid") return true;
	if (model.type === "upload") return Option.isNone(model.file);
	if (model.type === "tasks") return validTasks(model.tasks).length === 0;
	return model.body.trim() === "";
};

const toRequest = (model: Model): Option.Option<Request> | string => {
	if (model.occurredOn._tag === "Invalid") return model.occurredOn.errors[0];
	const tags = parseTags(model.tags);
	const occurredOn = Option.some(model.occurredOn.value).pipe(
		Option.filter((date) => date !== ""),
	);
	if (model.type === "upload") {
		return Option.match(model.file, {
			onNone: () => "Please choose a file to upload",
			onSome: (file) => Option.some(Request.Upload({ file, tags, occurredOn })),
		});
	}
	if (model.type === "tasks") {
		const tasks = validTasks(model.tasks);
		return tasks.length === 0
			? "Please add at least one task"
			: Option.some(
					Request.Tasks({ title: model.title.trim(), tasks, tags, occurredOn }),
				);
	}
	return model.body.trim() === ""
		? "Body cannot be empty"
		: Option.some(
				Request.Note({
					type: model.type,
					title: model.title,
					body: model.body,
					tags,
					occurredOn,
				}),
			);
};

const foldTags = Update.foldChild({
	update: (child: Tags.Model, message: Tags.Message) =>
		Tags.update(child, message),
	read: (model: Model) => Option.some(model.tagBox),
	write: (model: Model, tagBox: Tags.Model) =>
		evo(model, { tagBox: () => tagBox }),
	toParentMessage: (message: Tags.Message) =>
		Message.GotTagsMessage({ message }),
	foldOutMessage:
		(out: Tags.OutMessage) =>
		(model: Model): Return =>
			Tags.OutMessage.match(out, {
				// v1 `onAdd`: an invalid label shows the rule; a valid one joins the list.
				Selected: ({ value }) => {
					const tag = Tags.sanitizeTag(value);
					return {
						model:
							tag === undefined
								? evo(model, {
										tagError: () => Option.some(Tags.TAG_VALIDATION_MESSAGE),
									})
								: evo(model, {
										tagError: () => Option.none(),
										tags: (tags) => [...parseTags(tags), tag].join(", "),
									}),
					};
				},
				ClearedSelection: () => ({ model }),
			}),
});

export const update = (model: Model, message: Message): Return =>
	Message.match<Return>(message, {
		ToggledDate: () => ({
			model: { ...model, dateExpanded: !model.dateExpanded },
		}),
		ToggledTags: () => ({
			model: { ...model, tagsExpanded: !model.tagsExpanded },
		}),
		ToggledFormat: () => ({
			model: { ...model, formatExpanded: !model.formatExpanded },
		}),
		// v1 clears the error on a type change and drops a picked file when leaving "upload".
		ChangedType: ({ value }) => ({
			model: evo(model, {
				type: () => value,
				error: () => Option.none(),
				file: (file) => (value === "upload" ? file : Option.none()),
				title: (title) => (value === "upload" ? "" : title),
			}),
		}),
		ChangedTitle: ({ value }) => ({
			model: evo(model, { title: () => value }),
		}),
		ChangedBody: ({ value }) => ({ model: evo(model, { body: () => value }) }),
		ChangedTask: ({ index, value }) => ({
			model: evo(model, {
				tasks: (tasks) => tasks.map((task, i) => (i === index ? value : task)),
			}),
		}),
		ClickedAddTask: () => ({
			model: evo(model, { tasks: (tasks) => [...tasks, ""] }),
		}),
		ClickedRemoveTask: ({ index }) => ({
			model: evo(model, {
				tasks: (tasks) =>
					tasks.length === 1 ? [""] : tasks.filter((_, i) => i !== index),
			}),
		}),
		// v1 mirrors the file name into the (read-only) title.
		ChangedFile: ({ file }) => ({
			model: evo(model, {
				file: () => file,
				error: () => Option.none(),
				title: () =>
					Option.match(file, { onNone: () => "", onSome: (f) => f.name }),
			}),
		}),
		ChangedTags: ({ value }) => ({ model: evo(model, { tags: () => value }) }),
		ChangedOccurredOn: ({ value, today }) => ({
			model: evo(model, {
				occurredOn: () => validateOccurredOn(value, today),
				error: () => Option.none(),
			}),
		}),
		GotTagsMessage: ({ message }) => foldTags(model, message),
		ClickedRemoveTag: ({ tag }) => ({
			model: evo(model, {
				tagError: () => Option.none(),
				tags: (tags) =>
					parseTags(tags)
						.filter((t) => t !== tag)
						.join(", "),
			}),
		}),
		ClickedEditorAction: ({ action }) => ({
			model,
			commands: [Editor.EditorAction({ id: EDITOR_ID, action })],
		}),
		CompletedEditorAction: () => ({ model }),
		ChangedEditorFocus: () => ({ model }),
		Submitted: () => {
			if (model.submitting) return { model };
			const request = toRequest(model);
			if (typeof request === "string") {
				return { model: evo(model, { error: () => Option.some(request) }) };
			}
			return Option.match(request, {
				onNone: () => ({ model }),
				onSome: (r) => ({
					model: evo(model, {
						error: () => Option.none(),
						submitting: () => true,
					}),
					outMessage: OutMessage.Submitted({ request: r }),
				}),
			});
		},
	});

/** Page hooks for the Command lifecycle: success resets the form (v1 `resetFormState`). */
export const succeeded = (_model: Model): Model => init();
/** Whether the draft would still submit `request`: an in-flight save resets only an unedited draft. */
export const stillDrafts = (model: Model, request: Request): boolean =>
	Equal.equals(toRequest(model), Option.some(request));
export const failed = (model: Model, error: string): Model =>
	evo(model, { submitting: () => false, error: () => Option.some(error) });

// VIEW

export type ViewInputs = Readonly<{
	hasOtherEntries: boolean;
	dark: boolean;
	tagSuggestions: ReadonlyArray<string>;
}>;

/** The CodeMirror Mount (KTD6) with v1's toolbar; `dark` follows the root theme. */
const editorBody = (
	h: HtmlBuilder<Message>,
	model: Model,
	dark: boolean,
): Html =>
	h.div(
		[h.Class("entry-composer-editor w-full")],
		[
			h.div(
				[
					h.Id("entry-format-panel"),
					h.Class(model.formatExpanded ? "" : "hidden"),
				],
				[
					Editor.toolbar(h, (action) =>
						Message.ClickedEditorAction({ action }),
					),
				],
			),
			h.div([
				h.Key(EDITOR_ID),
				h.DataAttribute("testid", "editor-host"),
				h.OnMount(
					Mount.mapMessage(
						Editor.MarkdownEditor({
							id: EDITOR_ID,
							initialText: model.body,
							placeholder: i18next.t("entry.composer.placeholder"),
							dark,
							minHeight: 160,
						}),
						(message) =>
							Editor.Message.match<Message>(message, {
								EditorChanged: ({ text }) =>
									Message.ChangedBody({ value: text }),
								EditorFocusChanged: ({ focused }) =>
									Message.ChangedEditorFocus({ focused }),
							}),
					),
				),
			]),
		],
	);

/** The tag Combobox (KTD6) over the root's suggestions. */
const comboTags = (
	h: HtmlBuilder<Message>,
	model: Model,
	suggestions: ReadonlyArray<string>,
): Html =>
	Tags.view(h, {
		slotId: "add-entry-tags",
		model: model.tagBox,
		selected: parseTags(model.tags),
		suggestions,
		ariaLabel: "Tags",
		placeholder: "Add a tag and press Enter",
		error: model.tagError,
		toParentMessage: (message) => Message.GotTagsMessage({ message }),
		onRemove: (tag) => Message.ClickedRemoveTag({ tag }),
	});

/** v1 `AddEntryView.tsx` formatter: GB rounds to 1 decimal (deliberately distinct from `CreditUsage.tsx`'s formatter). */
export const formatFileSize = (size: number): string => {
	if (size < 1024) return `${size} B`;
	if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
	if (size < 1024 * 1024 * 1024)
		return `${(size / (1024 * 1024)).toFixed(1)} MB`;
	return `${(size / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

const typeSelect = (h: HtmlBuilder<Message>, model: Model, className: string) =>
	h.select(
		[
			h.Class(className),
			h.AriaLabel("Entry type"),
			h.Value(model.type),
			h.OnChange((value) =>
				Message.ChangedType({
					value: ENTRY_TYPES.find((t) => t === value) ?? "note",
				}),
			),
		],
		ENTRY_SUBTYPE_GROUPS.map(([groupKey, types]) =>
			h.optgroup(
				[h.Attribute("label", i18next.t(groupKey))],
				types.map((type) =>
					h.option(
						[h.Value(type), h.Selected(model.type === type)],
						[entrySubtypeLabel(type)],
					),
				),
			),
		),
	);

/** v1 `TasksInput` rows (disabled checkbox + input); the add/remove buttons are v2's. */
const tasksInput = (h: HtmlBuilder<Message>, model: Model) =>
	h.div(
		[h.Class("space-y-2 mb-2")],
		[
			...model.tasks.map((task, index) =>
				h.div(
					[h.Key(String(index)), h.Class("flex items-center gap-2")],
					[
						h.input([
							h.Type("checkbox"),
							h.Disabled(true),
							h.Class("checkbox checkbox-disabled"),
							h.AriaHidden(true),
						]),
						h.input([
							h.Type("text"),
							h.Class("input input-bordered flex-1"),
							h.Placeholder(`Task ${index + 1}`),
							h.AriaLabel(`Task ${index + 1}`),
							h.Value(task),
							h.OnInput((value) => Message.ChangedTask({ index, value })),
						]),
						h.button(
							[
								h.Type("button"),
								h.Class("btn btn-ghost btn-xs"),
								h.AriaLabel(`Remove task ${index + 1}`),
								h.OnClick(Message.ClickedRemoveTask({ index })),
							],
							["✕"],
						),
					],
				),
			),
			h.button(
				[
					h.Type("button"),
					h.Class("btn btn-ghost btn-sm"),
					h.AriaLabel("Add task"),
					h.OnClick(Message.ClickedAddTask()),
				],
				["+ Add task"],
			),
		],
	);

/** R1, R13: defaults to today (empty), capped at today; the max is read here, in the view. */
const occurredOnInput = (h: HtmlBuilder<Message>, model: Model) => {
	const today = localIsoDate(new Date());
	return h.div(
		[h.Class("mb-4")],
		[
			h.label(
				[h.For("entry-occurred-on"), h.Class("block font-semibold")],
				[i18next.t("entry.occurredOn")],
			),
			h.input([
				h.Id("entry-occurred-on"),
				h.Type("datetime-local"),
				h.Class("input input-bordered w-full"),
				h.Max(`${today}T23:59`),
				h.Value(model.occurredOn.value),
				h.OnChange((value) => Message.ChangedOccurredOn({ value, today })),
			]),
			model.occurredOn._tag === "Invalid"
				? h.p(
						[h.Role("alert"), h.Class("text-sm text-error mt-1")],
						[model.occurredOn.errors[0]],
					)
				: h.p(
						[
							h.Class("text-sm text-base-content/60 mt-1"),
							h.DataAttribute("testid", "occurred-on-hint"),
						],
						[i18next.t("entry.occurredOnHint")],
					),
		],
	);
};

const uploadInput = (h: HtmlBuilder<Message>, model: Model) =>
	h.div(
		[h.Class("flex flex-col gap-2 rounded-lg border border-base-300 p-4")],
		[
			h.input([
				h.Type("file"),
				h.Class("file-input file-input-bordered w-full"),
				h.AriaLabel("Choose a file"),
				h.OnFileChange((files) =>
					Message.ChangedFile({ file: Option.fromNullishOr(files[0]) }),
				),
			]),
			Option.match(model.file, {
				onNone: () =>
					h.span(
						[h.Class("text-sm text-base-content/60")],
						["Choose a file to attach to this entry"],
					),
				onSome: (file) =>
					h.span(
						[h.Class("text-sm text-base-content/70")],
						[`Selected: ${file.name} (${formatFileSize(file.size)})`],
					),
			}),
		],
	);

const composerText = (key: string) => i18next.t(`entry.composer.${key}`);

export const view = defineView<Model, Message, ViewInputs>(
	(model, inputs, h) => {
		const isTasks = model.type === "tasks";
		const isUpload = model.type === "upload";
		const dateOpen = model.dateExpanded || model.occurredOn._tag === "Invalid";
		const tagsOpen = model.tagsExpanded || Option.isSome(model.tagError);
		const toggle = (
			label: string,
			expanded: boolean,
			panel: string,
			message: Message,
		) =>
			h.button(
				[
					h.Type("button"),
					h.Class(
						`btn btn-ghost btn-sm gap-1.5 font-medium ${expanded ? "bg-base-200 text-base-content" : "text-base-content/65"}`,
					),
					h.AriaLabel(label),
					h.AriaExpanded(expanded),
					h.AriaControls(panel),
					h.OnClick(message),
				],
				[label],
			);
		return h.li(
			[h.DataAttribute("testid", "add-entry")],
			[
				h.div(
					[h.Class("timeline-middle")],
					[
						h.div(
							[
								h.Class(
									"flex h-8 w-8 items-center justify-center rounded-full border border-base-300 bg-base-100 text-primary",
								),
							],
							[CirclePlus(h, { size: 16 })],
						),
					],
				),
				h.div(
					[
						h.Class(
							`${inputs.hasOtherEntries ? "timeline-end" : "timeline-start"} mb-6 w-full min-w-0`,
						),
					],
					[
						h.form(
							[
								h.AriaLabel("Add entry"),
								h.OnSubmit(Message.Submitted()),
								h.DataAttribute("submit-entry-shortcut", ""),
								h.OnKeyDownPreventDefault(
									(key, { shiftKey, ctrlKey, metaKey, altKey }) =>
										key === "Enter" &&
										shiftKey &&
										!ctrlKey &&
										!metaKey &&
										!altKey
											? Option.some(Message.Submitted())
											: Option.none(),
								),
								h.Class(
									"entry-composer overflow-hidden rounded-xl border border-base-300 bg-base-100 shadow-sm transition-shadow focus-within:border-primary/40 focus-within:shadow-md",
								),
							],
							[
								h.div(
									[h.Class("flex items-center gap-3 px-4 pt-4 pb-2 sm:px-5")],
									[
										isTasks
											? h.span(
													[h.Class("flex-1 font-semibold")],
													[composerText("tasks")],
												)
											: h.input([
													h.Type("text"),
													h.Class(
														"entry-composer-title input min-w-0 flex-1 border-0 bg-transparent px-0 text-lg font-semibold shadow-none focus:outline-none",
													),
													h.Placeholder(
														isUpload ? "File title" : composerText("title"),
													),
													h.AriaLabel("Title"),
													h.Readonly(isUpload),
													h.Value(model.title),
													h.OnInput((value) => Message.ChangedTitle({ value })),
												]),
										typeSelect(
											h,
											model,
											"select select-sm w-32 shrink-0 border-base-300 bg-base-100 font-medium",
										),
									],
								),
								h.div(
									[h.Class("px-4 pb-2 sm:px-5")],
									[
										isTasks
											? tasksInput(h, model)
											: isUpload
												? uploadInput(h, model)
												: editorBody(h, model, inputs.dark),
									],
								),
								h.div(
									[
										h.Id("entry-date-panel"),
										h.Class(
											dateOpen
												? "border-t border-base-300 px-4 pt-4 sm:px-5"
												: "hidden",
										),
									],
									[occurredOnInput(h, model)],
								),
								isUpload
									? h.empty
									: h.div(
											[
												h.Id("entry-tags-panel"),
												h.Class(
													tagsOpen
														? "border-t border-base-300 p-4 sm:px-5"
														: "hidden",
												),
											],
											[
												h.div(
													[h.Class("text-sm font-medium mb-2")],
													[composerText("tags")],
												),
												comboTags(h, model, inputs.tagSuggestions),
											],
										),
								Option.match(model.error, {
									onNone: () => h.empty,
									onSome: (error) =>
										h.p(
											[
												h.Role("alert"),
												h.Class("px-4 py-3 text-sm text-error sm:px-5"),
											],
											[error],
										),
								}),
								h.div(
									[
										h.Class(
											"flex flex-wrap items-center justify-between gap-3 border-t border-base-300 bg-base-200/30 px-3 py-3 sm:px-4",
										),
									],
									[
										h.div(
											[h.Class("flex flex-wrap items-center gap-1")],
											[
												toggle(
													model.occurredOn.value || composerText("date"),
													dateOpen,
													"entry-date-panel",
													Message.ToggledDate(),
												),
												isUpload
													? h.empty
													: toggle(
															`${composerText("tags")}${parseTags(model.tags).length ? ` · ${parseTags(model.tags).length}` : ""}`,
															tagsOpen,
															"entry-tags-panel",
															Message.ToggledTags(),
														),
												isTasks || isUpload
													? h.empty
													: toggle(
															composerText("format"),
															model.formatExpanded,
															"entry-format-panel",
															Message.ToggledFormat(),
														),
											],
										),
										h.span(
											[h.Class("ml-auto text-xs text-base-content/50")],
											[composerText("submitShortcut")],
										),
										h.button(
											[
												h.Type("submit"),
												h.Attribute("aria-keyshortcuts", "Shift+Enter"),
												h.Class("btn btn-primary btn-sm min-h-10 px-5"),
												h.Disabled(isSubmitDisabled(model)),
												h.AriaLabel(
													model.submitting ? "Adding entry" : "Add entry",
												),
											],
											[
												model.submitting
													? composerText("adding")
													: composerText("add"),
											],
										),
									],
								),
							],
						),
					],
				),
				inputs.hasOtherEntries ? h.hr([]) : h.empty,
			],
		);
	},
);
